import React from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { DEFAULT_BRANDING_EMAIL } from "@root/constants/branding";

interface EmailTemplatesTabProps {
  config: IBrandingConfig | null;
  onSave: (emailBranding: IBrandingEmailBranding) => Promise<void>;
  saving: boolean;
}

const emailSchema = Yup.object().shape({
  fromName: Yup.string().required("Sender name is required"),
  fromEmail: Yup.string()
    .email("Invalid email address")
    .required("Sender email is required"),
  footerText: Yup.string(),
});

export const EmailTemplatesTab: React.FC<EmailTemplatesTabProps> = ({
  config,
  onSave,
  saving,
}) => {
  const { t } = useTranslation("common");

  const initialValues: IBrandingEmailBranding =
    config?.emailBranding || DEFAULT_BRANDING_EMAIL;

  return (
    <Box p="16px">
      <Text fontWeight={600} fontSize="18px" mb="16px">
        {t("admin.branding.email.title")}
      </Text>

      <Formik
        initialValues={initialValues}
        validationSchema={emailSchema}
        enableReinitialize
        onSubmit={(values) => onSave(values)}
      >
        {({ handleSubmit, handleChange, values, errors, touched }) => (
          <form onSubmit={handleSubmit}>
            <Flex flexDir="column" gap="24px" maxW="600px">
              <FormControl
                isInvalid={!!errors.fromName && touched.fromName}
              >
                <FormLabel fontSize="14px" fontWeight={500}>
                  {t("admin.branding.email.fromName")}
                </FormLabel>
                <Input
                  name="fromName"
                  value={values.fromName}
                  onChange={handleChange}
                  borderRadius="8px"
                />
                {errors.fromName && touched.fromName && (
                  <Text color="red.500" fontSize="12px" mt="4px">
                    {errors.fromName}
                  </Text>
                )}
              </FormControl>

              <FormControl
                isInvalid={!!errors.fromEmail && touched.fromEmail}
              >
                <FormLabel fontSize="14px" fontWeight={500}>
                  {t("admin.branding.email.fromEmail")}
                </FormLabel>
                <Input
                  name="fromEmail"
                  value={values.fromEmail}
                  onChange={handleChange}
                  type="email"
                  borderRadius="8px"
                />
                {errors.fromEmail && touched.fromEmail && (
                  <Text color="red.500" fontSize="12px" mt="4px">
                    {errors.fromEmail}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="14px" fontWeight={500}>
                  {t("admin.branding.email.footerText")}
                </FormLabel>
                <Textarea
                  name="footerText"
                  value={values.footerText || ""}
                  onChange={handleChange}
                  borderRadius="8px"
                  rows={4}
                  placeholder="Optional footer text for branded emails"
                />
              </FormControl>

              <Flex justify="flex-end">
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={saving}
                  px="32px"
                >
                  {t("admin.branding.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        )}
      </Formik>
    </Box>
  );
};
